import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getInvoice } from '../services/invoicesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function InvoiceDetailPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDoc(await getInvoice(type, id));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando comprobante…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!doc) return <EmptyState title="Comprobante no encontrado" />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/comprobantes">Comprobantes</Link> / {doc.tipoLabel} / {doc.serie}-{doc.correlativo}
          </p>
          <h1>
            {doc.serie}-{doc.correlativo}
          </h1>
          <p className="muted">
            {doc.tipoLabel} · {formatDate(doc.fechaEmision)} · {doc.rznSocial || doc.numDoc || 'Cliente'}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
          {doc.pdf ? (
            <a className="btn btn-primary" href={doc.pdf} target="_blank" rel="noreferrer">
              Descargar PDF
            </a>
          ) : null}
          {doc.xml ? (
            <a className="btn btn-secondary" href={doc.xml} target="_blank" rel="noreferrer">
              Descargar XML
            </a>
          ) : null}
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">Total</p>
          <p className="stat-value">{formatMoney(doc.total)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Subtotal</p>
          <p className="stat-value" style={{ fontSize: '1.1rem' }}>
            {formatMoney(doc.subtotal ?? doc.subTotal)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">IGV</p>
          <p className="stat-value" style={{ fontSize: '1.1rem' }}>
            {formatMoney(doc.igv)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">SUNAT</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            <Badge tone={doc.aceptado ? 'ok' : 'warn'}>{doc.aceptado ? 'Aceptado' : 'No aceptado'}</Badge>
          </p>
        </div>
      </section>

      <section className="meta-grid">
        <div>
          <span className="muted">Tipo doc SUNAT</span>
          <div>{doc.tipoDoc || '—'}</div>
        </div>
        <div>
          <span className="muted">Cliente</span>
          <div>{doc.numDoc || '—'}</div>
        </div>
        <div>
          <span className="muted">Razón social</span>
          <div>{doc.rznSocial || '—'}</div>
        </div>
        <div>
          <span className="muted">Pedido</span>
          <div>
            {doc.pedido ? (
              <Link to={`/admin/ventas`}>#{doc.pedido}</Link>
            ) : (
              '—'
            )}
          </div>
        </div>
        <div>
          <span className="muted">Estado interno</span>
          <div>{doc.status || '—'}</div>
        </div>
        <div>
          <span className="muted">Vencimiento</span>
          <div>{formatDate(doc.fecVencimiento)}</div>
        </div>
        {doc.numDocfectado ? (
          <div>
            <span className="muted">Doc. afectado</span>
            <div>
              {doc.numDocfectado} {doc.desMotivo ? `· ${doc.desMotivo}` : ''}
            </div>
          </div>
        ) : null}
        {doc.cdr ? (
          <div>
            <span className="muted">CDR</span>
            <div>
              <a href={doc.cdr} target="_blank" rel="noreferrer">
                Descargar CDR
              </a>
            </div>
          </div>
        ) : null}
      </section>

      <p className="muted">
        La emisión, reenvío y consulta a SUNAT siguen en la infraestructura existente
        (MotorNumeracion / apiPeruProxy). Este panel no reimplementa facturación.
      </p>
    </div>
  );
}
